/**
 * `Drawer` — right-side slide-over overlay (`feat/dry-run-results-ux`
 * fix 1). Mirrors the dashboard's existing right-slide idiom
 * (`MobileDrawer`) so the visual language stays consistent: full-height
 * panel anchored to the viewport's right edge, dark backdrop, thin 1px
 * left border, framer-motion tween.
 *
 * Why an atom rather than reusing `Modal`: the dry-run "View mandate"
 * surface is not a confirmation dialog — it's a long-form detail panel
 * the operator scrolls through (audit record, settlement, line items,
 * buyer reveal). A centered modal forces a cramped layout and clips
 * vertically; a right-anchored drawer lets the detail breathe while the
 * underlying rules grid stays visible behind the scrim.
 *
 * Accessibility baseline (kept minimal — no new deps):
 *   - `role="dialog"` + `aria-modal="true"` on the panel.
 *   - Esc key closes (window listener installed only while `open`).
 *   - Backdrop click closes (matches `Modal` + `MobileDrawer`).
 *   - Focus moves into the panel on open (the close button is the
 *     first focusable element, matching the visual flow).
 *
 * Sizing:
 *   - Desktop (sm+): fixed width `max-w-xl` so the panel reads like a
 *     side rail. Long-form detail content scrolls inside the body.
 *   - Mobile (< sm): full-viewport sheet (`w-full`), matching the
 *     spec's "Mobile: full-width sheet" requirement.
 */

import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

export interface DrawerProps {
  /** When false the component renders `null`. The parent owns mount/unmount
   *  so a closing drawer can animate out via `AnimatePresence` if desired. */
  open: boolean;
  /** Called on Esc, backdrop click, and the close affordance. */
  onClose: () => void;
  children: ReactNode;
  /** Visible heading label above the close affordance (mono kicker).
   *  Optional — when absent the drawer omits its header bar. */
  title?: ReactNode;
  /** Stable test id forwarded to the panel root so suites can target it. */
  'data-testid'?: string;
  /** Override the default `aria-label` on the close button. Defaults to
   *  `"Close"`. Used by the dry-run pivot to keep the historical
   *  `"Close mandate detail"` label (which tests anchor on). */
  closeLabel?: string;
}

export function Drawer({
  open,
  onClose,
  children,
  title,
  'data-testid': testid,
  closeLabel = 'Close',
}: DrawerProps): JSX.Element | null {
  const C = useC();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Esc-to-close: install only while the drawer is open so a stale
  // listener doesn't fire after unmount.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus into the panel on open. The first focusable child (the close
  // button) is the natural landing point; falling back to the panel
  // itself keeps keyboard users anchored inside the drawer for the Tab
  // sequence even when no interactive children exist yet.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel === null) return;
    // Defer one frame so the panel is in the DOM and the close button
    // (rendered in the header) is mountable before we hunt for it.
    const handle = requestAnimationFrame(() => {
      const focusTarget = panel.querySelector<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      );
      (focusTarget ?? panel).focus();
    });
    return () => cancelAnimationFrame(handle);
  }, [open]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      {...(testid !== undefined ? { 'data-testid': testid } : {})}
    >
      <motion.div
        ref={panelRef}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        // `tabIndex={-1}` lets us programmatically focus the panel as a
        // last-resort focus target without it appearing in the natural
        // Tab sequence.
        tabIndex={-1}
        // `fix/dry-run-tooltip-mobile-hover` ITEM 2 — clamp the panel
        // to the viewport on mobile. `w-full` alone proved insufficient
        // when the panel's content (long mono mandate_ids, canonical
        // JSON, masked credentials) introduced an intrinsic width
        // larger than the viewport — the panel would then stretch to
        // its content's width and overflow the screen, producing a
        // horizontal page scroll. `max-w-[100vw]` pins the upper bound
        // on mobile; the body's `overflow-x-hidden` below contains
        // content that still wants to be wide (long tokens wrap; the
        // canonical JSON `<pre>` scrolls horizontally within its own
        // box). Desktop (`sm:max-w-xl`) wins on ≥ 640px viewports
        // because `36rem` < `100vw` at that breakpoint, so the side
        // rail keeps its prior look.
        className="ml-auto flex flex-col w-full max-w-[100vw] sm:max-w-xl"
        style={{
          background: C.bg,
          borderLeft: `1px solid ${C.border}`,
          outline: 'none',
        }}
        data-testid="drawer-panel"
      >
        <div
          className="flex items-center justify-between px-4 sm:px-6 py-3 flex-shrink-0"
          style={{
            borderBottom: `1px solid ${C.border}`,
            background: C.surface,
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title ?? ''}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="flex items-center gap-1 px-2 py-1 flex-shrink-0 ml-3"
            style={{
              background: 'transparent',
              color: C.muted,
              border: `1px solid ${C.border}`,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              borderRadius: 2,
            }}
          >
            <X size={11} /> CLOSE
          </button>
        </div>
        {/* `fix/dry-run-tooltip-mobile-hover` ITEM 2 — `overflow-x-hidden`
            companion to the long-standing `overflow-y-auto` so wide
            content (mono mandate_ids, masked credentials, mono kicker
            rows) is clipped INSIDE the body rather than pushing the
            panel past the viewport edge. Inner scrollable boxes (e.g.
            the canonical-JSON `<pre>` in `MandateDetail`) keep their
            own horizontal scroll because that scroll lives on the
            element itself, not on this body. */}
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden"
          data-testid="drawer-body"
        >
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
}
